import React, { useEffect, useState, useRef } from "react";
import { Button, Container, Form, Modal } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import { useUser } from "../../../apis/user/UserProvider";
import { updatePasswordService, UsuarioApiResponse, UsuarioPutVM } from "../../../services/userService";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import { useRole } from '../../../apis/role/RoleProvider';
import WarehouseSection from "../../../components/WarehouseSection";
import warehouseClient from "../../../types/warehouseClient";

const ViewUserById: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { fetchUsuarioById, putUsuario, excluiUsuario } = useUser();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { roles, fetchRole } = useRole();
    const [user, setUser] = useState<UsuarioApiResponse | null>(null);
    const [fullName, setFullName] = useState("");
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [accountExpired, setAccountExpired] = useState(false);
    const [accountLocked, setAccountLocked] = useState(false);
    const [enabled, setEnabled] = useState(true);
    const [passwordExpired, setPasswordExpired] = useState(false);
    const [role, setRole] = useState("");
    const [userWarehouses, setUserWarehouses] = useState<warehouseClient[]>([]);
    const [selectedWarehouses, setSelectedWarehouses] = useState<number[]>([]);

    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");

    useEffect(() => {
        const loadUser = async () => {
            try {
                setLoading(true);
                setError(false);

                if (!id) throw new Error("ID inválido");

                const result = await fetchUsuarioById(Number(id));

                if (!result) {
                    throw new Error("Usuário não encontrado");
                }

                setUser(result);

                const userData = result.data;

                setUserWarehouses(userData.worksTo || []);
                setFullName(userData.fullName);
                setUsername(userData.username);
                setEmail(userData.email);
                setPassword(userData.password);
                setAccountExpired(userData.accountExpired);
                setAccountLocked(userData.accountLocked);
                setEnabled(userData.enabled);
                setPasswordExpired(userData.passwordExpired);
                setRole(userData.secUserSecRoles && userData.secUserSecRoles.length > 0 
                    ? String(userData.secUserSecRoles[0].secRole.id) 
                    : "");
            } catch (err) {
                console.error("Erro ao carregar os dados:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        loadUser();
    }, [id, fetchUsuarioById]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                await fetchRole();
            } catch (err) {
                console.error("Erro ao carregar roles", err);
            }
        };

        fetchData();
    }, []);

    const fullNameRef = useRef<HTMLInputElement | null>(null);

    const navItems: NavBarItem[] = [
        { label: t('user.labels.home'), route: "/", iconName: "house-door" },
        { label: t('user.labels.list'), route: "/User", iconName: "list" },
        { label: t('user.labels.new'), route: "/User/create", iconName: "plus-circle" },
    ];

    useEffect(() => {
        if (isEditing && fullNameRef.current) {
            fullNameRef.current.focus();
        }
    }, [isEditing]);

    const preparePayload = (): UsuarioPutVM => {
        return {
            id: user?.data.id || 0,
            fullName,
            password,
            userName: username,
            email,
            enabled,
            accountExpired,
            accountLocked,
            passwordExpired,
            roleIds: role ? [Number(role)] : [],
            wareHouseClientIds: selectedWarehouses
        };
    };

    const handleEdit = async () => {
        if (!isEditing) {
            setIsEditing(true);
            return;
        }

        try {
            if (!user) return;

            const payload = preparePayload();
            if (!payload) return;

            await putUsuario(user.data.id, payload);

            const successMessage = getSuccessMessage("editUser", { id: user.data.id, });
            navigate(`/User`, {
                state: {
                    message: successMessage,
                    variant: "success",
                },
            });

        } catch (error) {
            console.error("Erro ao atualizar os dados:", error);
            alert("Erro ao atualizar os dados.");
        } finally {
            setIsEditing(false);
        }
    };

    const handleDelete = async () => {
        if (!id) return;
        try {
            const sucess = await excluiUsuario(Number(id));
            if (sucess) {
                const successMessage = getSuccessMessage("deleteUser", { id: id, });
                navigate(`/User`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            } else {
                 const errorMessage = getErrorMessage("deleteUsuarioError", { id: id, });
                navigate(`/User`, {
                    state: {
                        message: errorMessage,
                        variant: "danger",
                    },
                });
            }
            


        } catch (error) {
            alert("Erro ao excluir o registro.");
        }
    };

    if (loading) return <div className="text-center mt-4">{t('loading')}</div>;
    if (error || !user) return <div className="text-center mt-4 text-danger">{t('errorLoadingData')}</div>;

    const handlePasswordChange = async () => {
    // Validações básicas
        if (!currentPassword || !newPassword || !confirmNewPassword) {
            setPasswordError(t('passwordError.required'));
            return;
        }
        
        if (newPassword !== confirmNewPassword) {
            setPasswordError(t('passwordError.match'));
            return;
        }
        
        if (newPassword.length < 6) {
            setPasswordError(t('passwordError.length'));
            return;
        }
        
        setPasswordError("");
        
        try {
            if (!user?.data.id) throw new Error("ID do usuário não encontrado");
            
            await updatePasswordService(currentPassword, newPassword, user.data.id);
            
            // Limpa os campos e fecha o modal
            setCurrentPassword("");
            setNewPassword("");
            setConfirmNewPassword("");
            setShowPasswordModal(false);
            
            // Mostra mensagem de sucesso
            setSuccessMessage(t('passwordUpdated'));
        } catch (error) {
            console.error("Erro ao alterar senha:", error);
            setPasswordError(t('passwordError.update'));
        }
    };

    return (
        <Container fluid>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{isEditing ? t('pageName.editUser') : t('pageName.viewUser')}</h2>

            {successMessage && (
                <div style={{ fontSize: "14px", color: "green", marginTop: "5px" }}>
                    {successMessage}
                </div>
            )}

            <Form className="mt-3">
                <div className="d-flex gap-4 mb-3">
                    <Form.Group style={{ width: "20%" }}>
                        <Form.Label>{t('user.fields.id')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={user?.data?.id || ""}
                            readOnly
                            style={{ backgroundColor: "#f0f0f0" }}
                        />
                    </Form.Group>
                </div>

                <div className="d-flex gap-4 mb-3">
                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.name')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={fullName}
                            ref={fullNameRef}
                            readOnly={!isEditing}
                            style={{ backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            onChange={(e) => setFullName(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.username')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={username}
                            readOnly={!isEditing}
                            style={{ backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                    </Form.Group>
                </div>

                <div className="d-flex gap-4 mb-3">
                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.password')}</Form.Label>
                        <Form.Control
                            type="password"
                            value={password}
                            readOnly={!isEditing}
                            style={{ backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.email')}</Form.Label>
                        <Form.Control
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            readOnly={!isEditing}
                            style={{ backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                        />
                    </Form.Group>

                    <Form.Group style={{ width: "30%" }}>
                        <Form.Label>{t('user.fields.role')}</Form.Label>
                        <Form.Select
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            disabled={!isEditing}
                        >
                            <option value="">{t('user.fields.roleSelect')}</option>
                            {roles?.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.authority}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>

                <Form.Group className="d-flex gap-4 mb-3">
                    <Form.Check
                        type="checkbox"
                        label={t('user.fields.accountExpired')}
                        checked={accountExpired}
                        onChange={(e) => setAccountExpired(e.target.checked)}
                        disabled={!isEditing}
                        style={{
                            fontWeight: accountExpired ? 'bold' : 'normal',
                            color: accountExpired ? '#000000' : '#6c757d',
                            opacity: 1
                        }}
                    />
                    <Form.Check
                        type="checkbox"
                        label={t('user.fields.accountLocked')}
                        checked={accountLocked}
                        onChange={(e) => setAccountLocked(e.target.checked)}
                        disabled={!isEditing}
                        style={{
                            fontWeight: accountLocked ? 'bold' : 'normal',
                            color: accountLocked ? '#000000' : '#6c757d',
                            opacity: 1
                        }}
                    />
                    <Form.Check
                        type="checkbox"
                        label={t('user.fields.enabled')}
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        disabled={!isEditing}
                        style={{
                            fontWeight: enabled ? 'bold' : 'normal',
                            color: enabled ? '#000000' : '#6c757d',
                            opacity: 1
                        }}
                    />
                    <Form.Check
                        type="checkbox"
                        label={t('user.fields.passwordExpired')}
                        checked={passwordExpired}
                        onChange={(e) => setPasswordExpired(e.target.checked)}
                        disabled={!isEditing}
                        style={{
                            fontWeight: passwordExpired ? 'bold' : 'normal',
                            color: passwordExpired ? '#000000' : '#6c757d',
                            opacity: 1
                        }}
                    />
                </Form.Group>

                <WarehouseSection
                    selectedWarehouses={selectedWarehouses}
                    setSelectedWarehouses={setSelectedWarehouses}
                    isEditing={isEditing}
                    userWarehouses={userWarehouses}
                />

            </Form>

            <div className="mt-3 mb-3 d-flex gap-2">
                <Button variant="primary" onClick={handleEdit}>
                    {isEditing ? getUiInterface("save") : getUiInterface("edit")}
                </Button>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{getUiInterface("delete")}</Button>
                {/* <Button variant="warning" onClick={() => setShowPasswordModal(true)}>
                    <i className="bi bi-key-fill me-2"></i>
                    Alterar Senha
                </Button> */}
            </div>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
            />

            <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Alterar Senha</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Senha Atual</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder="Digite sua senha atual"
                                />
                            </Form.Group>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>Nova Senha</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Digite a nova senha"
                                />
                            </Form.Group>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>Confirmar Nova Senha</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={confirmNewPassword}
                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                    placeholder="Confirme a nova senha"
                                />
                                {passwordError && (
                                    <div className="text-danger mt-2">{passwordError}</div>
                                )}
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
                            Cancelar
                        </Button>
                        <Button variant="primary" onClick={handlePasswordChange}>
                            Salvar Nova Senha
                        </Button>
                    </Modal.Footer>
                </Modal>
        </Container>
    );
};

export default ViewUserById;